// Create a deep nested edit view for Slider and its translated items
import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Switch, Button, Card, Space, message, Tabs, Col, Row, Upload, Typography } from 'antd';
import { UploadOutlined, PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const { Title } = Typography;

const SliderEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];


    const { data: sliderData, isLoading } = useQuery({
        queryKey: ['adminSlider', id],
        queryFn: async () => {
            const response = await apiClient.get(`/admin/sliders/${id}`);
            return response.data.data;
        },
        enabled: !isNew
    });

    useEffect(() => {
        if (sliderData) {
            // Transform data structure to match form structure
            const formValues = {
                name: sliderData.name,
                autoplaySpeed: sliderData.autoplaySpeed,
                showDots: sliderData.showDots,
                showArrows: sliderData.showArrows,
                items: sliderData.items?.map((item: any) => ({
                    id: item.id,
                    imageUrl: item.imageUrl,
                    orderIndex: item.orderIndex,
                    translations: item.translations
                })) || []
            };
            form.setFieldsValue(formValues);
        } else if (isNew) {
            form.setFieldsValue({
                autoplaySpeed: 5000,
                showDots: true,
                showArrows: true,
                items: []
            });
        }
    }, [sliderData, isNew, form]);

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            // Transform form items for submission
            const payload = {
                ...values,
                items: values.items?.map((item: any) => ({
                    id: item.id || null,
                    imageUrl: item.imageUrl,
                    orderIndex: item.orderIndex || 0,
                    translations: appLanguages.reduce((acc: any, lang) => {
                        acc[lang] = item.translations?.[lang] || {};
                        return acc;
                    }, {})

                })) || []
            };

            if (isNew) {
                const res = await apiClient.post('/admin/sliders', payload);
                return res.data;
            } else {
                const res = await apiClient.put(`/admin/sliders/${id}`, { id, ...payload });
                return res.data;
            }
        },
        onSuccess: () => {
            message.success('Slider erfolgreich gespeichert');
            queryClient.invalidateQueries({ queryKey: ['adminSliders'] });
            navigate('/admin/sliders');
        },
        onError: () => {
            message.error('Fehler beim Speichern');
        }
    });

    const onFinish = (values: any) => {
        mutation.mutate(values);
    };

    const handleCustomRequest = async (options: any, fieldName: number) => {
        const { file, onSuccess, onError } = options;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const imgUrl = response.data.data.url;
            
            // Update the specific field in the form array manually
            const items = form.getFieldValue('items') || [];
            items[fieldName] = { ...items[fieldName], imageUrl: imgUrl };
            form.setFieldsValue({ items });

            onSuccess('Ok');
            message.success('Bild hochgeladen');
        } catch (error) {
            onError({ error });
            message.error('Upload fehlgeschlagen');
        }
    };

    if (isLoading && !isNew) return <div>Laden...</div>;

    return (
        <Card 
            title={
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/sliders')} />
                    {isNew ? 'Neuen Slider erstellen' : 'Slider bearbeiten'}
                </Space>
            }
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="name" label="Interner Name" rules={[{ required: true }]}>
                            <Input placeholder="z.B. Hero Homepage" />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="autoplaySpeed" label="Autoplay Speed (ms)">
                            <InputNumber min={0} className="w-full" />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="showDots" label="Punkte anzeigen" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="showArrows" label="Pfeile anzeigen" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>

                <Title level={4} className="mt-8 mb-4">Slides verwalten</Title>
                
                <Form.List name="items">
                    {(fields, { add, remove }) => (
                        <div className="space-y-6 flex flex-col gap-4">
                            {fields.map((field, index) => (
                                <Card size="small" key={field.key} className="bg-gray-50">
                                    <div className="flex justify-between items-center mb-4">
                                        <strong>Slide #{index + 1}</strong>
                                        <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                    </div>
                                    
                                    <Form.Item name={[field.name, 'id']} hidden><Input /></Form.Item>
                                    
                                    <Row gutter={24}>
                                        <Col span={8}>
                                            <Form.Item 
                                                name={[field.name, 'imageUrl']} 
                                                label="Bild URL" 
                                                rules={[{ required: true, message: 'Bild ist erforderlich' }]}
                                            >
                                                <Input readOnly />
                                            </Form.Item>
                                            <Upload 
                                                customRequest={(options) => handleCustomRequest(options, field.name)} 
                                                showUploadList={false}
                                            >
                                                <Button icon={<UploadOutlined />}>Bild Hochladen</Button>
                                            </Upload>
                                            <Form.Item name={[field.name, 'orderIndex']} label="Sortierung" className="mt-4">
                                                <InputNumber min={0} className="w-full" />
                                            </Form.Item>
                                        </Col>
                                        
                                        <Col span={16}>
                                             <Tabs
                                                items={appLanguages.map(lang => ({
                                                    key: lang,
                                                    label: lang === 'de' ? 'Deutsch' : lang === 'tr' ? 'Türkçe' : lang === 'en' ? 'English' : lang === 'ru' ? 'Русский' : 'العربية',
                                                    children: (
                                                        <div className="space-y-4">
                                                            <Form.Item name={[field.name, 'translations', lang, 'title']} label="Titel / Title">
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item name={[field.name, 'translations', lang, 'subtitle']} label="Untertitel / Subtitle">
                                                                <Input />
                                                            </Form.Item>
                                                            <Row gutter={16}>
                                                                <Col span={12}>
                                                                    <Form.Item name={[field.name, 'translations', lang, 'buttonText']} label="Button Text">
                                                                        <Input />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col span={12}>
                                                                    <Form.Item name={[field.name, 'translations', lang, 'buttonUrl']} label="Button URL">
                                                                        <Input placeholder="/projekte" />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    )
                                                }))}
                                            />

                                        </Col>
                                    </Row>
                                </Card>
                            ))}
                            
                            <Button type="dashed" onClick={() => add({ orderIndex: fields.length })} block icon={<PlusOutlined />}>
                                Slide hinzufügen
                            </Button>
                        </div>
                    )}
                </Form.List>

                <div className="mt-8 flex justify-end">
                    <Button type="primary" htmlType="submit" size="large" loading={mutation.isPending}>
                        Speichern
                    </Button>
                </div>
            </Form>
        </Card>
    );
};

export default SliderEdit;
